import { useEffect, useState } from "react";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
import { blobToBase64 } from "@/utils/string.ts";
import { useDownloadFileQuery } from "@/data/file.ts";
import { Tabs, TabsList, TabsTrigger } from "@edusogno/primitives/tabs.js";
import { CorrectionQResults, ExerciseDONE, ExerciseResult } from "@/types";
import cn from "classnames";
import jsPDF from "jspdf";
import Spinner from "@edusogno/primitives/spinner";
import PDFViewer from "@/components/ui/PDFViewer.tsx";
import AnswerSlot from "../exercises/AnswerSlot.tsx";
import CaDownloadAnswerIcon from "@/components/icons/exerciseV2/CaDownloadAnswerIcon.tsx";
import ImgViewer from "@/components/ui/ImgViewer.tsx";

interface AnswerSheetProps {
  exercise?: ExerciseResult | ExerciseDONE;
  renderSlots?: (
    exercise:
      | [string, { question_type: string; correct_answer: string }][]
      | undefined,
  ) => React.ReactNode;
  id?: number;
}

interface IPdfCache {
  [key: string]: string | null;
}

function ReadingCorrectionAnswerSheet({
  id,
  exercise,
  renderSlots,
}: AnswerSheetProps) {
  const [pdf, setPdf] = useState<string | null>(null);
  const [fileType, setFileType] = useState("media1");
  const [pdfCache, setPdfCache] = useState<IPdfCache>({});
  const [activeTab, setActiveTab] = useState("Questions");
  const [questionType, setQuestionType] = useState("question1");
  const [questionsPdf, setQuestionsPdf] = useState<string | null>(null);
  const [questionsPdfCache, setQuestionsPdfCache] = useState<IPdfCache>({});
  const [imagesGroup, setImagesGroup] = useState<string[] | null>(null);
  const [qImagesGroup, setQImagesGroup] = useState<string[] | null>(null);

  const { isLoading: isPDFLoading, blob } = useDownloadFileQuery(
    { id: (id as number) ?? exercise?.ex_id, type: fileType },
    fileType !== undefined && !pdfCache[fileType],
  );

  const images = exercise?.files?.images && exercise.files.images.length > 0 ? exercise.files.images : null;
  const qImages = exercise?.files?.["q-images"] && exercise.files?.["q-images"].length > 0 ? exercise.files?.["q-images"] : null;

  const { blob: questionsBlob } = useDownloadFileQuery(
    { id: (id as number) ?? exercise?.ex_id, type: questionType },
    questionType !== undefined && !questionsPdfCache[questionType],
  );

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
  };

  const handleFileChange = (type: string) => {
    setFileType(type);
    if (pdfCache[type]) {
      setPdf(pdfCache[type]);
    }

    if (type === "media1") {
      setImagesGroup(images && images[0]);
    } else if (type === "media2") {
      setImagesGroup(images && images[1]);
    } else if (type === "media3") {
      setImagesGroup(images && images[2]);
    } else {
      setImagesGroup(null);
    }
  };

  const handleQuestionChange = (type: string) => {
    setQuestionType(type);
    if (questionsPdfCache[type]) {
      setQuestionsPdf(questionsPdfCache[type]);
    }

    if (type === "question1") {
      setQImagesGroup(qImages && qImages[0]);
    } else if (type === "question2") {
      setQImagesGroup(qImages && qImages[1]);
    } else if (type === "question3") {
      setQImagesGroup(qImages && qImages[2]);
    } else {
      setQImagesGroup(null);
    }
  };

  useEffect(() => {
    if (!blob || pdfCache[fileType]) return;
    void (async () => {
      const result = await blobToBase64(blob);
      setPdf(result);
      setPdfCache((prev) => ({ ...prev, [fileType]: result }));

      const idx = fileType === "media1" ? 0 : fileType === "media2" ? 1 : 2;
      setImagesGroup(images && images[idx]);
    })();
  }, [blob, fileType, pdfCache, images]);

  useEffect(() => {
    if (!questionsBlob || questionsPdfCache[questionType]) return;
    void (async () => {
      const result = await blobToBase64(questionsBlob);
      setQuestionsPdf(result);
      setQuestionsPdfCache((prev) => ({ ...prev, [questionType]: result }));

      const idx = questionType === "question1" ? 0 : questionType === "question2" ? 1 : 2;
      setQImagesGroup(qImages && qImages[idx]);
    })();
  }, [questionsBlob, questionType, questionsPdfCache, qImages]);

  const arrayParts = [
    { text: "Part 1", mediaValue: "media1", questionValue: "question1" },
    { text: "Part 2", mediaValue: "media2", questionValue: "question2" },
    { text: "Part 3", mediaValue: "media3", questionValue: "question3" },
  ];

  const handleDownload = () => {
    if (
      !exercise ||
      !exercise.test_name ||
      !exercise.modality ||
      !exercise.type ||
      !(exercise as ExerciseResult).result
    ) {
      console.error("Exercise data is incomplete.");
      return;
    }

    const exerciseResults = Object.entries(
      (exercise as ExerciseResult).result,
    ).map(([key, value]) => {
      if (typeof value === "string") {
        // Handle case where value is a string
        return {
          question: key,
          correctAnswer: "-",
          userAnswer: value, // Treat string as userAnswer
        };
      } else if (typeof value === "object" && value !== null) {
        // Handle case where value is the expected object
        return {
          question: key,
          correctAnswer: value.correct_answer || "-",
          userAnswer: value.user_answer || "-",
        };
      } else {
        // Default fallback for unexpected cases
        return {
          question: key,
          correctAnswer: "-",
          userAnswer: "-",
        };
      }
    });

    const title = exercise.test_name;
    const modality = exercise.modality;
    const type = exercise.type;

    const doc = new jsPDF();

    // Header Section
    doc.setFontSize(16);
    doc.text(type, 10, 10);
    doc.setFontSize(12);
    doc.text(modality, 10, 14);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text(title, 10, 25);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(10, 26, doc.internal.pageSize.width - 10, 26);

    // Answer Sheet Label
    const rectX = 10;
    const rectY = 30;
    const rectWidth = 50;
    const rectHeight = 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.setFillColor(0, 0, 0);
    doc.roundedRect(rectX, rectY, rectWidth, rectHeight, 2, 2, "FD");

    // Calculate text width and position to center the text horizontally
    const text = "Answer Sheet";
    const textWidth = doc.getTextWidth(text);
    const textX = rectX + (rectWidth - textWidth) / 2;

    // Calculate text position to center it vertically
    const textY = rectY + (rectHeight + 3) / 2;

    doc.setTextColor(255, 255, 255);
    doc.text(text, textX, textY);

    // Correct, Incorrect, Blank labels with dots
    const legendStartX = doc.internal.pageSize.width - 100;
    doc.setTextColor(0, 0, 0);

    doc.setFillColor(0, 128, 0);
    doc.circle(legendStartX, 34, 2, "F");
    doc.text("Correct", legendStartX + 5, 36);

    doc.setFillColor(255, 0, 0);
    doc.circle(legendStartX + 30, 34, 2, "F");
    doc.text("Incorrect", legendStartX + 35, 36);

    doc.setFillColor(128, 128, 128);
    doc.circle(legendStartX + 60, 34, 2, "F");
    doc.text("Blank", legendStartX + 65, 36);

    // Questions and Answers Section
    let yPosition = 50;
    const lineHeight = 10;
    const pageHeight = doc.internal.pageSize.height;

    exerciseResults.forEach((result) => {
      if (yPosition + lineHeight > pageHeight) {
        doc.addPage();
        yPosition = 20;
      }

      // Question text in black
      doc.setTextColor(0, 0, 0);
      doc.text(`Question ${result.question}:`, 10, yPosition);

      if (result.userAnswer == "N/A") {
        // Render correct answer fully in green
        doc.setTextColor(0, 128, 0);
        doc.text(result?.correctAnswer, 35, yPosition);
      } else {
        // Render user_answer in red, and the rest in black
        const userAnswerWidth = doc.getTextWidth(result.userAnswer);

        // Render userAnswer in red
        doc.setTextColor(255, 0, 0);
        doc.text(result.userAnswer, 35, yPosition);

        // Render dash and correctAnswer in black
        doc.setTextColor(0, 0, 0);
        doc.text(
          ` - ${result.correctAnswer}`,
          35 + userAnswerWidth + 2,
          yPosition,
        );
      }

      yPosition += lineHeight;
    });

    doc.save(`${title}.pdf`);
  };

  return (
    <div className="flex flex-col xl:flex-row gap-4 w-full flex-[1_1_0] min-h-0 h-full">
      <div className="flex flex-col w-full h-full until-xl:overflow-y-auto">
        <div className="flex flex-col w-full min-h-full gap-4 xl:flex-row">
          {/* PDF SECTION */}
          <section
            className={cn(
              "rounded-[10px] flex flex-col h-full",
              "until-xl:min-h-[30vh] xl:min-h-auto w-full flex-[1_1_100%]",
            )}
          >
            <Tabs defaultValue="Part 1" className="flex gap-3">
              {arrayParts.map((part) => (
                <TabsList
                  key={part.text}
                  className="p-0 rounded-[10px] h-[30px] md:h-[30px]"
                >
                  <TabsTrigger
                    value={part.text}
                    onClick={() => {
                      handleFileChange(part.mediaValue);
                      handleQuestionChange(part.questionValue);
                    }}
                    className="active:border-none active:outline-0 h-[30px] md:h-[30px] px-4 lg:w-[60px] data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    {part.text}
                  </TabsTrigger>
                </TabsList>
              ))}
            </Tabs>

            <div
              className={cn(
                "rounded-[10px] flex flex-col xl:flex-row w-full h-full bg-white mt-4 min-h-0",
                "until-xl:min-h-[30vh] xl:min-h-auto flex-[1_1_100%]",
              )}
            >
              {isPDFLoading && !imagesGroup ? (
                <div className="flex items-center justify-center w-full h-full col-span-3">
                  <Spinner className="!h-12 !w-12 border-t-light" />
                </div>
              ) : (
                // <PDFViewer fileBase64={pdf} />
                imagesGroup ? (
                  <ImgViewer images={imagesGroup} />
                ) : (
                  <PDFViewer fileBase64={pdf} />
                )
              )}
            </div>
          </section>

          {/* QUESTIONS/ANSWER SHEET SECTION */}
          <section className="flex flex-col flex-grow w-full min-h-full gap-4 until-xl:pt-11">
            <div className="flex flex-row items-center justify-between">
              <Tabs
                defaultValue="Questions"
                className="flex items-center justify-between w-full"
              >
                <TabsList className="p-0 rounded-[10px] w-[240px] h-[30px] md:h-[30px]">
                  <TabsTrigger
                    onClick={() => handleTabChange("Questions")}
                    value="Questions"
                    className="active:border-none active:outline-0 lg:w-[120px] h-[30px] md:h-[30px] xl:px-4 data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    Questions
                  </TabsTrigger>

                  <TabsTrigger
                    onClick={() => handleTabChange("Answer_sheet")}
                    value="Answer sheet"
                    className="active:border-none active:outline-0 lg:w-[120px] h-[30px] md:h-[30px] px-[25px] xl:px-4 data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    Answer sheet
                  </TabsTrigger>
                </TabsList>

                {activeTab === "Answer_sheet" && (
                  <button
                    onClick={handleDownload}
                    className="h-[30px] md:h-[30px] w-[30px] rounded-[10px] bg-[#D3EADD] flex justify-center items-center"
                  >
                    <CaDownloadAnswerIcon />
                  </button>
                )}
              </Tabs>
            </div>

            <div
              className={cn(
                "rounded-[10px] flex flex-col xl:flex-row w-full h-full bg-white min-h-0",
                { hidden: activeTab !== "Questions" },
              )}
            >
              {/* <PDFViewer fileBase64={questionsPdf} /> */}

              {qImagesGroup ? (
                <ImgViewer images={qImagesGroup} />
              ) : (
                <PDFViewer fileBase64={questionsPdf} />
              )}
            </div>

            <div
              className={cn("rounded-[10px] w-full h-full min-h-0", {
                hidden: activeTab !== "Answer_sheet",
              })}
            >
              {renderSlots ? (
                renderSlots(
                  exercise?.questions && Object.entries(exercise.questions),
                )
              ) : (
                <ScrollArea className="h-full min-h-0 ">
                  <div
                    id="form-ex"
                    className="grid grid-cols-[repeat(auto-fill,minmax(247px,auto))] gap-[12px] w-full"
                  >
                    {exercise?.questions &&
                      Object.entries((exercise as ExerciseResult).result).map(
                        ([key, q]) => (
                          <AnswerSlot
                            readonly
                            key={key}
                            number={key}
                            {...(q as unknown as CorrectionQResults)}
                          />
                        ),
                      )}
                  </div>
                </ScrollArea>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ReadingCorrectionAnswerSheet;
